"use client";

import { Camera, Video, Sparkles, Check, Zap, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import type { AvatarCreationMode } from "./types";

interface Step1Props {
    selected: AvatarCreationMode | null;
    onSelect: (mode: AvatarCreationMode) => void;
}

interface ModeMeta {
    id: AvatarCreationMode;
    title: string;
    description: string;
    Icon: React.ComponentType<{ className?: string }>;
    badge?: { label: string; Icon: React.ComponentType<{ className?: string }> };
}

export default function Step1ChooseMode({ selected, onSelect }: Step1Props) {
    const { locale } = useLocale();
    const MODES: ModeMeta[] = [
        {
            id: "photo",
            title: t("avatars.wizard.step1.photo.title", locale),
            description: t("avatars.wizard.step1.photo.description", locale),
            Icon: Camera,
            badge: {
                label: t("avatars.wizard.step1.badge.fastest", locale),
                Icon: Zap,
            },
        },
        {
            id: "video",
            title: t("avatars.wizard.step1.video.title", locale),
            description: t("avatars.wizard.step1.video.description", locale),
            Icon: Video,
            badge: {
                label: t("avatars.wizard.step1.badge.most-realistic", locale),
                Icon: ShieldCheck,
            },
        },
        {
            id: "prompt",
            title: t("avatars.wizard.step1.prompt.title", locale),
            description: t("avatars.wizard.step1.prompt.description", locale),
            Icon: Sparkles,
        },
    ];
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {MODES.map(({ id, title, description, Icon, badge }) => {
                const isSelected = selected === id;
                const BadgeIcon = badge?.Icon;
                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => onSelect(id)}
                        aria-pressed={isSelected}
                        className={cn(
                            "relative group p-4 rounded-xl border bg-background text-start flex flex-col gap-2.5 transition-all",
                            "hover:border-primary/60 hover:bg-muted/30",
                            isSelected
                                ? "border-primary ring-2 ring-primary/30 bg-primary/[0.03]"
                                : "border-border"
                        )}
                    >
                        {isSelected && (
                            <span className="absolute top-2 end-2 size-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                                <Check className="size-3" />
                            </span>
                        )}
                        <div
                            className={cn(
                                "size-9 rounded-lg flex items-center justify-center",
                                isSelected
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-primary/10 text-primary"
                            )}
                        >
                            <Icon className="size-5" />
                        </div>
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm leading-tight">
                                    {title}
                                </span>
                                {badge && BadgeIcon && (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                                        <BadgeIcon className="size-2.5" />
                                        {badge.label}
                                    </span>
                                )}
                            </div>
                            <span className="text-xs text-muted-foreground leading-relaxed">
                                {description}
                            </span>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}
